import React, { useState, useMemo } from 'react';
import axios from 'axios';

// Las tasas que ofrece el desplegable. Cualquier otra entra por «Personalizado».
const TASAS_HABITUALES = ['5', '10', '15', '20', '25', '30'];
const esPersonalizado = (tasa) =>
  !TASAS_HABITUALES.includes(String(tasa ?? 10));

function NewLoan({ clients, settings, onSuccess }) {
  const [searchClient, setSearchClient] = useState('');
  const [client_id, setClientId] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [amount, setAmount] = useState('');
  const [interestRate, setInterestRate] = useState((settings.interest_rate || 10).toString());
  // Si el interés por defecto de la empresa no está entre los del desplegable,
  // hay que arrancar en «Personalizado». Sin esto el <select> no encontraba la
  // opción y el navegador mostraba la primera (5%) mientras el préstamo se
  // creaba con el valor real: la pantalla decía una cosa y se guardaba otra.
  const [isCustomRate, setIsCustomRate] = useState(() => esPersonalizado(settings.interest_rate));
  const [frequency, setFrequency] = useState(settings.frequency || 'monthly');
  const [loanStartDate, setLoanStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [maturityDate, setMaturityDate] = useState('');
  // Préstamo abierto: sin fecha de fin pactada. El cliente paga el interés
  // cada periodo y salda el capital cuando pueda.
  const [sinVencimiento, setSinVencimiento] = useState(false);
  // La modalidad del cobradiario: cuota fija en vez de interés que se liquida
  // en el corte. Arranca apagada — quien ya usa el programa no debe encontrarse
  // con una casilla nueva encendida que le cambie las cuentas.
  const [cuotaFija, setCuotaFija] = useState(false);
  const [cuota, setCuota] = useState('');
  // El fiador va en el préstamo, no en el cliente: la misma persona puede
  // tener un préstamo con fiador y otro sin él.
  const [tieneFiador, setTieneFiador] = useState(false);
  const [fiador, setFiador] = useState({ nombre: '', cedula: '', telefono: '', direccion: '', email: '' });
  const [preview, setPreview] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const filteredClients = searchClient.trim()
    ? clients.filter(c =>
        c.name.toLowerCase().includes(searchClient.toLowerCase()) ||
        (c.cedula && c.cedula.includes(searchClient))
      )
    : [];

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setClientId(client.id);
    setSearchClient(client.name);
    setShowSuggestions(false);
  };

  const handleRateChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setIsCustomRate(true);
      setInterestRate('');
    } else {
      setIsCustomRate(false);
      setInterestRate(value);
    }
  };

  const handleCustomRateChange = (e) => {
    setInterestRate(e.target.value);
  };

  const moneda = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');
  const COMO_PAGA = { daily: 'diarias', weekly: 'semanales',
                      biweekly: 'quincenales', monthly: 'mensuales' };

  // Lo que va a deber y en cuántas cuotas, mientras se teclea. Es la misma
  // cuenta del cobradiario: total pactado desde el día uno, dividido en cuotas
  // iguales; la última puede salir más chica si no da exacto.
  const resumenCuotaFija = useMemo(() => {
    const capital = parseFloat(amount);
    const tasa = parseFloat(interestRate);
    const valor = parseFloat(cuota);
    if (!cuotaFija || !Number.isFinite(capital) || capital <= 0 ||
        !Number.isFinite(tasa) || !Number.isFinite(valor) || valor <= 0) return null;
    const total = capital * (1 + tasa / 100);
    if (valor > total) return null;
    const cuotas = Math.ceil(total / valor);
    const ultima = Math.round(total - (cuotas - 1) * valor);
    return {
      capital, total: Math.round(total), cuotas,
      comoPaga: COMO_PAGA[frequency] || 'mensuales',
      ultima: ultima === Math.round(valor) ? 0 : ultima,
    };
  }, [cuotaFija, amount, interestRate, cuota, frequency]);

  const handleCalculatePreview = () => {
    if (!client_id || !amount || (!maturityDate && !sinVencimiento)) {
      alert('Completa cliente, monto y fecha de vencimiento');
      return;
    }

    // En un préstamo abierto no hay cuántos cobros ni total: solo la cuota que
    // se va a cobrar cada periodo hasta que salde el capital.
    if (sinVencimiento) {
      const interesAbierto = parseFloat(amount) * (parseFloat(interestRate) / 100);
      setPreview({
        abierto: true,
        frequencyText: ({ daily: 'diario', weekly: 'semanal', biweekly: 'quincenal' })[frequency] || 'mensual',
        interestPerPayment: interesAbierto,
        loanAmount: parseFloat(amount)
      });
      return;
    }

    const rate = parseFloat(interestRate) / 100;
    const loanAmount = parseFloat(amount);
    const interest = loanAmount * rate;

    // Se añade la hora para que las fechas se lean como medianoche local.
    // Con `new Date('2026-09-28')` JavaScript asume UTC y la fecha mostrada
    // retrocede un día en husos negativos como el de Colombia.
    const start = new Date(`${loanStartDate}T00:00:00`);
    const end = new Date(`${maturityDate}T00:00:00`);
    const days = Math.round((end - start) / 86400000);

    if (days <= 0) {
      alert('La fecha de vencimiento debe ser posterior a la de inicio');
      return;
    }

    // Cuántas veces se cobra el interés entre ambas fechas. Antes eran
    // constantes (30, 4, 2, 1), así que un préstamo a dos meses mostraba la
    // ganancia de uno solo.
    let frequencyText;
    let paymentCycles;

    switch (frequency) {
      case 'daily':
        frequencyText = 'diario';
        paymentCycles = days;
        break;
      case 'weekly':
        frequencyText = 'semanal';
        paymentCycles = Math.floor(days / 7);
        break;
      case 'biweekly':
        frequencyText = 'quincenal';
        paymentCycles = Math.floor(days / 15);
        break;
      default:
        frequencyText = 'mensual';
        paymentCycles =
          (end.getFullYear() - start.getFullYear()) * 12 +
          (end.getMonth() - start.getMonth()) -
          (end.getDate() < start.getDate() ? 1 : 0);
    }

    paymentCycles = Math.max(1, paymentCycles);

    setPreview({
      loanAmount,
      interest,
      totalGain: interest * paymentCycles,
      totalToReceive: loanAmount + interest * paymentCycles,
      frequencyText,
      interestPerPayment: interest,
      paymentCycles,
      days
    });
  };

  const handleCreateLoan = async (e) => {
    e.preventDefault();

    // La fecha solo hace falta si el préstamo NO es abierto. Esta comprobación
    // se quedó exigiéndola siempre cuando se añadió la casilla, así que marcarla
    // dejaba el formulario bloqueado sin poder crear nada.
    if (!client_id || !amount || (!maturityDate && !sinVencimiento)) {
      alert('Completa cliente, monto y fecha de vencimiento (o marca que es un préstamo abierto)');
      return;
    }

    // La tasa NO se estaba revisando, y el hueco costó plata de verdad.
    //
    // Con el campo vacío, `parseFloat('')` da NaN, sale como nulo en el JSON y
    // el préstamo queda guardado al 0% sin que nada lo diga. Medido el
    // 2026-08-16 en la cartera de un prestamista: 21 préstamos así,
    // $83.860.000, seis de ellos creados ese mismo mes. Uno de $25.000.000
    // llevaba 19 meses sin generar un peso de interés.
    //
    // No es un error que se note: el préstamo aparece normal en todas las
    // pantallas, con su saldo y sus abonos. Simplemente no cobra.
    let tasa = parseFloat(interestRate);

    // Lo que se pierde al dejarlo en cero, dicho con lo que de verdad pasa.
    //
    // No es «no gana intereses» —eso ya se sabe— sino que el préstamo
    // DESAPARECE de las dos pantallas donde se cobra: Por cobrar muestra lo que
    // hay que cobrar este periodo, y en un 0% eso es cero; Morosos mide a quién
    // le falta pagar, y no le falta nada. Solo queda en Acuerdos.
    //
    // Por eso el aviso nombra las tres pantallas: quien lo lee tiene que poder
    // imaginarse el lunes buscando a ese cliente y no encontrándolo.
    const AVISO_CERO =
      'OJO: al 0% este préstamo NO va a aparecer en «Por cobrar» ni en ' +
      '«Morosos».\n\nSolo lo va a ver en «Acuerdos», que es la lista de los ' +
      'que quedaron en devolver el capital.';

    // En blanco NO se rechaza a secas: se pregunta. Dejarlo vacío casi siempre
    // es un olvido, pero a veces es la forma de decir «este no cobra», y
    // mandar al usuario de vuelta sin preguntarle no distingue las dos.
    if (!Number.isFinite(tasa)) {
      if (!window.confirm(
        'Dejaste el interés en blanco.\n\n' +
        '¿Este préstamo va a quedar al 0%?\n\n' + AVISO_CERO + '\n\n' +
        'Aceptar = dejarlo al 0%\nCancelar = volver y escribir el interés'
      )) return;
      tasa = 0;
    } else if (tasa < 0) {
      alert('El interés no puede ser negativo.');
      return;
    } else if (tasa === 0 && !window.confirm(
      // El cero escrito a propósito también se pregunta, con el mismo aviso:
      // que esté escrito no quiere decir que se supiera lo que implica.
      '¿Seguro que este préstamo NO cobra interés?\n\n' + AVISO_CERO + '\n\n' +
      'Si fue un error, cancela y escribe la tasa.'
    )) return;

    try {
      await axios.post('/api/loans', {
        client_id: parseInt(client_id),
        amount: parseFloat(amount),
        interest_rate: tasa,
        frequency,
        start_date: loanStartDate,
        maturity_date: sinVencimiento
          ? null
          : new Date(`${maturityDate}T00:00:00`).toISOString(),
        // Si se desmarca la casilla no se manda nada, aunque hubiera algo
        // escrito: lo que se ve en pantalla es lo que se guarda.
        fiador_nombre: tieneFiador ? fiador.nombre : null,
        fiador_cedula: tieneFiador ? fiador.cedula : null,
        fiador_telefono: tieneFiador ? fiador.telefono : null,
        fiador_direccion: tieneFiador ? fiador.direccion : null,
        fiador_email: tieneFiador ? fiador.email : null,
        modalidad: cuotaFija ? 'cuota_fija' : null,
        cuota: cuotaFija ? parseFloat(cuota) : null
      });
      alert('✅ Préstamo creado correctamente');
      setSearchClient('');
      setClientId('');
      setSelectedClient(null);
      setCuotaFija(false);
      setCuota('');
      setAmount('');
      setInterestRate((settings.interest_rate || 10).toString());
      setIsCustomRate(esPersonalizado(settings.interest_rate));
      setFrequency(settings.frequency || 'monthly');
      setLoanStartDate(new Date().toISOString().split('T')[0]);
      setMaturityDate('');
      setSinVencimiento(false);
      setTieneFiador(false);
      setFiador({ nombre: '', cedula: '', telefono: '', direccion: '', email: '' });
      setPreview(null);
      onSuccess();
    } catch (err) {
      alert('❌ Error al crear préstamo: ' + err.message);
    }
  };

  return (
    <div className="new-loan-page">
      <h2>➕ Prestar</h2>

      <div className="form-card">
        <form onSubmit={handleCreateLoan}>

          {/* Búsqueda de Cliente */}
          <div className="form-group">
            <label>🔍 Buscar Cliente (Nombre o Cédula) *</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Ej: Juan López o 1234567890"
                value={searchClient}
                onChange={(e) => {
                  setSearchClient(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => searchClient && setShowSuggestions(true)}
              />

              {showSuggestions && searchClient && filteredClients.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  marginTop: '5px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 10,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  {filteredClients.map(client => (
                    <div
                      key={client.id}
                      onClick={() => handleSelectClient(client)}
                      style={{
                        padding: '10px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        hover: { background: '#f9fafb' }
                      }}
                    >
                      <strong>{client.name}</strong>
                      {client.cedula && <span style={{ color: '#6b7280', marginLeft: '10px' }}>({client.cedula})</span>}
                      {client.phone && <div style={{ fontSize: '12px', color: '#6b7280' }}>{client.phone}</div>}
                    </div>
                  ))}
                </div>
              )}

              {showSuggestions && searchClient && filteredClients.length === 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  marginTop: '5px',
                  padding: '10px',
                  color: '#991b1b',
                  fontSize: '14px',
                  zIndex: 10
                }}>
                  ❌ No se encontraron clientes
                </div>
              )}
            </div>
            {selectedClient && (
              <small style={{ color: '#10b981' }}>
                ✅ Cliente: {selectedClient.name} {selectedClient.cedula && `(${selectedClient.cedula})`}
              </small>
            )}
          </div>

          <div className="form-group">
            <label>Monto del Préstamo ($) *</label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Ej: 1000"
              required
            />
          </div>

          <div className="form-group">
            <label>Porcentaje de Interés (%) *</label>
            <select value={isCustomRate ? 'custom' : interestRate} onChange={handleRateChange}>
              {TASAS_HABITUALES.map(v => (
                <option key={v} value={v}>{v}%</option>
              ))}
              <option value="custom">📝 Personalizado</option>
            </select>

            {isCustomRate && (
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                placeholder="Ingresa tu porcentaje"
                value={interestRate}
                onChange={handleCustomRateChange}
                style={{ width: '100%', marginTop: '10px', padding: '8px', borderRadius: '4px' }}
                autoFocus
              />
            )}
            <small>{interestRate && !isCustomRate ? `Interés: ${interestRate}%` : isCustomRate && interestRate ? `Interés: ${interestRate}%` : ''}</small>
          </div>

          <div className="form-group">
            <label>Frecuencia de Pago *</label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
            >
              <option value="daily">Diario</option>
              <option value="weekly">Semanal</option>
              <option value="biweekly">Quincenal</option>
              <option value="monthly">Mensual</option>
            </select>
          </div>

          <div className="form-group">
            <label>📅 Fecha de Inicio del Préstamo *</label>
            <input
              type="date"
              value={loanStartDate}
              onChange={(e) => setLoanStartDate(e.target.value)}
              required
            />
          </div>

          {/* La forma de cobrar del cobradiario: se pacta el total desde el
              primer día y el cliente da una cuota fija hasta completar. No es
              una variante de lo otro, es otro negocio, y por eso se elige aquí
              y no se deduce de nada. */}
          <div className="form-group">
            <label style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              cursor: 'pointer', fontWeight: 'normal'
            }}>
              <input type="checkbox" checked={cuotaFija}
                     onChange={(e) => setCuotaFija(e.target.checked)}
                     style={{ width: 'auto' }} />
              <span>💵 Cuota fija — el cliente da siempre lo mismo hasta completar</span>
            </label>

            {cuotaFija && (
              <div style={{
                marginTop: '10px', padding: '14px', background: '#f0fdf4',
                border: '1px solid #bbf7d0', borderRadius: '8px'
              }}>
                <label style={{ display: 'block', marginBottom: '6px' }}>
                  ¿De cuánto es cada cuota? *
                </label>
                <input type="number" value={cuota} min="1" inputMode="numeric"
                       onChange={(e) => setCuota(e.target.value)}
                       placeholder="4000"
                       style={{ maxWidth: '14rem' }} />
                {resumenCuotaFija && (
                  <div style={{ marginTop: '10px', fontSize: '14px', color: '#166534' }}>
                    Le entrega <strong>{moneda(resumenCuotaFija.capital)}</strong> y le
                    debe <strong>{moneda(resumenCuotaFija.total)}</strong> desde hoy
                    {' · '}<strong>{resumenCuotaFija.cuotas}</strong> cuotas
                    {' '}{resumenCuotaFija.comoPaga}
                    {resumenCuotaFija.ultima > 0 &&
                      ` (la última de ${moneda(resumenCuotaFija.ultima)})`}
                  </div>
                )}
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#4b5563' }}>
                  Con la cuota fija no hay corte mensual: la deuda es una resta
                  y el atraso se cuenta en cuotas.
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>📅 Fecha de Vencimiento {!sinVencimiento && '*'}</label>
            <input
              type="date"
              value={sinVencimiento ? '' : maturityDate}
              onChange={(e) => setMaturityDate(e.target.value)}
              disabled={sinVencimiento}
              required={!sinVencimiento}
              style={sinVencimiento ? { background: '#f3f4f6', color: '#9ca3af' } : undefined}
            />
            {maturityDate && !sinVencimiento && (
              <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                Vence: {new Date(`${maturityDate}T00:00:00`).toLocaleDateString('es-CO')}
              </small>
            )}

            {/* Muchos préstamos no tienen fecha de fin: se paga el interés
                hasta que el cliente pueda saldar. Obligar una fecha hace que
                se invente, y esa fecha falsa aparece luego en Vencimientos
                como un compromiso que nadie pactó. */}
            <label style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              cursor: 'pointer', marginTop: '10px', fontWeight: 'normal'
            }}>
              <input
                type="checkbox"
                checked={sinVencimiento}
                onChange={(e) => setSinVencimiento(e.target.checked)}
                style={{ width: 'auto', margin: 0 }}
              />
              Préstamo abierto, sin fecha de vencimiento
            </label>

            {sinVencimiento && (
              <div style={{
                background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px',
                padding: '11px 13px', marginTop: '8px', fontSize: '13px', color: '#1e40af'
              }}>
                Cobrarás el interés cada periodo hasta que salde el capital. No
                aparecerá en <strong>Vencimientos</strong> ni en
                <strong> Estadísticas</strong>, porque no hay fecha que vigilar
                — pero sí en Morosos si se atrasa, y en Deuda con lo que lleva
                acumulado.
              </div>
            )}
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={tieneFiador}
                onChange={(e) => setTieneFiador(e.target.checked)}
                style={{ width: 'auto', margin: 0 }}
              />
              🤝 Este préstamo tiene fiador
            </label>
          </div>

          {tieneFiador && (
            <div style={{
              background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px',
              padding: '15px', marginBottom: '15px'
            }}>
              <div style={{ fontWeight: 'bold', color: '#374151', marginBottom: '10px', fontSize: '14px' }}>
                Datos del fiador
              </div>
              {[
                ['nombre', 'Nombre completo', 'text'],
                ['cedula', 'Cédula', 'text'],
                ['telefono', 'Teléfono', 'tel'],
                ['direccion', 'Dirección', 'text'],
                ['email', 'Correo', 'email']
              ].map(([campo, etiqueta, tipo]) => (
                <div className="form-group" key={campo}>
                  <label>{etiqueta}</label>
                  <input
                    type={tipo}
                    value={fiador[campo]}
                    onChange={(e) => setFiador(prev => ({ ...prev, [campo]: e.target.value }))}
                  />
                </div>
              ))}
              <small style={{ color: '#6b7280' }}>
                Aparecerán en Morosos si este cliente se atrasa, que es cuando
                hacen falta.
              </small>
            </div>
          )}

          <button
            type="button"
            className="btn-secondary"
            onClick={handleCalculatePreview}
          >
            📋 Calcular Vista Previa
          </button>

          <button type="submit" className="btn-primary">
            ✅ Crear Préstamo
          </button>
        </form>
      </div>

      {preview && (
        <div className="preview-card">
          <h3>📊 Vista Previa del Préstamo</h3>
          <div className="preview-details">
            <p><strong>Cliente:</strong> {selectedClient?.name}</p>
            <p><strong>Monto Prestado:</strong> ${preview.loanAmount.toFixed(2)}</p>
            {/* En un préstamo abierto no hay duración ni total: enseñar un
                número ahí sería inventarse un final que nadie pactó. */}
            {preview.abierto ? (
              <>
                <p><strong>Duración:</strong> abierto, sin fecha de fin</p>
                <p><strong>Interés por Pago ({preview.frequencyText}):</strong> ${preview.interestPerPayment.toFixed(2)}</p>
                <p className="earning">
                  <strong>Ganancia:</strong> ${preview.interestPerPayment.toFixed(2)} cada cobro,
                  mientras no abone al capital
                </p>
              </>
            ) : (
              <>
                <p><strong>Duración:</strong> {preview.days} días</p>
                <p><strong>Interés por Pago ({preview.frequencyText}):</strong> ${preview.interestPerPayment.toFixed(2)}</p>
                <p><strong>Número de Cobros:</strong> {preview.paymentCycles}</p>
                <p><strong>Total a Recibir:</strong> ${preview.totalToReceive.toFixed(2)}</p>
                <p className="earning"><strong>Ganancia Total:</strong> ${preview.totalGain.toFixed(2)}</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NewLoan;
